import React, { useState, useRef } from "react";

/**
 * VoiceInput - A microphone button that records audio and sends it to the Whisper server.
 * 
 * Props:
 *   onTranscript(text) - callback with transcribed text
 *   language - optional language hint (e.g. "bn" for Bangla, "en" for English)
 *   append - if true, appends to existing text; if false, replaces
 */
const WHISPER_URL = window._env_?.WHISPER_URL || "http://localhost:9000";
const WHISPER_API_KEY = window._env_?.WHISPER_API_KEY || "ehr-transcribe-secret-key";

function VoiceInput({ onTranscript, language, append = true }) {
    const [recording, setRecording] = useState(false);
    const [processing, setProcessing] = useState(false);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
            mediaRecorderRef.current = mediaRecorder;
            chunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };

            mediaRecorder.onstop = async () => {
                // Stop all tracks to release mic
                stream.getTracks().forEach(track => track.stop());

                const blob = new Blob(chunksRef.current, { type: "audio/webm" });
                setProcessing(true);

                try {
                    const formData = new FormData();
                    formData.append("audio", blob, "recording.webm");

                    const langParam = language ? `?language=${language}` : "";
                    const response = await fetch(`${WHISPER_URL}/transcribe${langParam}`, {
                        method: "POST",
                        headers: { "X-API-Key": WHISPER_API_KEY },
                        body: formData
                    });

                    if (response.ok) {
                        const data = await response.json();
                        if (data.text && onTranscript) {
                            onTranscript(data.text, append);
                        }
                    } else {
                        console.error("Whisper server error:", response.status);
                    }
                } catch (err) {
                    console.error("Failed to reach Whisper server:", err);
                } finally {
                    setProcessing(false);
                }
            };

            mediaRecorder.start();
            setRecording(true);
        } catch (err) {
            console.error("Microphone access denied:", err);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
            mediaRecorderRef.current.stop();
            setRecording(false);
        }
    };

    const handleClick = () => {
        if (recording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    return (
        <button
            type="button"
            className={`voice-input-btn ${recording ? "voice-input-recording" : ""} ${processing ? "voice-input-processing" : ""}`}
            onClick={handleClick}
            disabled={processing}
            title={recording ? "Stop recording" : processing ? "Transcribing..." : "Start voice input"}
            aria-label={recording ? "Stop recording" : "Start voice input"}
        >
            {processing ? "⏳" : recording ? "⏹" : "🎤"}
        </button>
    );
}

export default VoiceInput;
